import React, { useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  Bell,
  CheckSquare,
  ChevronDown,
  DollarSign,
  FileText,
  Inbox,
  Info,
  Menu,
  ShoppingCart,
  LogOut,
  Package,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/logo.png';
import { Notification } from '../lib/notificationApi';

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function NotificationIcon({ type }: { type: string }) {
  const iconClass = 'flex-shrink-0';
  switch (type) {
    case 'rfq_received':
      return <Inbox className={`${iconClass} text-blue-500`} size={15} />;
    case 'rfq_processed':
      return <CheckSquare className={`${iconClass} text-blue-500`} size={15} />;
    case 'quotation_generated':
      return <FileText className={`${iconClass} text-indigo-500`} size={15} />;
    case 'quotation_needs_review':
      return <AlertCircle className={`${iconClass} text-amber-500`} size={15} />;
    case 'confirmed_order_created':
      return <CheckSquare className={`${iconClass} text-blue-600`} size={15} />;
    case 'vendor_po_created':
      return <Package className={`${iconClass} text-purple-500`} size={15} />;
    case 'client_invoice_created':
      return <DollarSign className={`${iconClass} text-cyan-500`} size={15} />;
    case 'vendor_invoice_received':
    case 'vendor_invoice_matched':
      return <ShoppingCart className={`${iconClass} text-orange-500`} size={15} />;
    default:
      return <Info className={`${iconClass} text-slate-400`} size={15} />;
  }
}

interface NotificationItemProps {
  notification: Notification;
  onRead: (id: string) => void;
  onNavigate: (link: string | null) => void;
}

function NotificationItem({ notification, onRead, onNavigate }: NotificationItemProps) {
  const handleClick = () => {
    if (!notification.isRead) {
      onRead(notification._id);
    }
    if (notification.link) {
      onNavigate(notification.link);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`w-full text-left flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-slate-50 ${
        !notification.isRead ? 'bg-blue-50/60' : ''
      }`}
    >
      <div className="mt-0.5">
        <NotificationIcon type={notification.type} />
      </div>
      <div className="flex-1 min-w-0">
        <p
          className={`text-sm leading-snug truncate ${
            !notification.isRead ? 'font-semibold text-slate-800' : 'font-medium text-slate-700'
          }`}
        >
          {notification.title}
        </p>
        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{notification.message}</p>
        <p className="text-[10px] text-slate-400 mt-1">{formatRelativeTime(notification.createdAt)}</p>
      </div>
      {!notification.isRead && (
        <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-1.5" />
      )}
    </button>
  );
}

interface TopBarProps {
  /** Opens the mobile sidebar drawer — omitted/no-op above the md breakpoint, where
   * the hamburger button itself is hidden. */
  onMenuClick?: () => void;
}

export const TopBar: React.FC<TopBarProps> = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const { notifications, unreadCount, isLoading, markAsRead, markAllRead } = useNotifications();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);

  const badgeCount = unreadCount > 99 ? '99+' : String(unreadCount);

  useEffect(() => {
    if (!showDropdown && !showNotifications) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowDropdown(false);
        setShowNotifications(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showDropdown, showNotifications]);

  useEffect(() => {
    if (!showNotifications) return;
    const onDown = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showNotifications]);

  const handleLogout = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDropdown(false);
    setShowNotifications(false);
    logout();

    navigate('/login', { replace: true });
    setTimeout(() => {
      if (window.location.pathname !== '/login') window.location.replace('/login');
    }, 50);
  };

  const handleNotificationNavigate = (link: string | null) => {
    setShowNotifications(false);
    if (link) navigate(link);
  };

  const getUserInitials = () => {
    if (!user) return 'U';
    const firstInitial = user.firstName?.charAt(0) || '';
    const lastInitial = user.lastName?.charAt(0) || '';
    return (firstInitial + lastInitial).toUpperCase() || user.email?.charAt(0).toUpperCase() || 'U';
  };

  const getUserDisplayName = () => {
    if (!user) return 'User';
    return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'User';
  };

  const getRoleDisplayName = () => {
    if (!user) return 'User';
    return user.role === 'admin' ? 'Administrator' : 'User';
  };

  return (
    <div className="relative z-[45] no-print">
      <header
        className="relative h-16 min-h-[4rem] flex items-center justify-between px-4 sm:px-6 border-b border-slate-200 bg-white overflow-visible"
        style={{ paddingTop: 'env(safe-area-inset-top, 0)' }}
      >
        <div className="flex items-center gap-2 min-w-0 -ml-2 sm:-ml-3">
          <button
            type="button"
            onClick={onMenuClick}
            className="md:hidden p-2 rounded-lg hover:bg-slate-100 text-slate-600 shrink-0"
            aria-label="Open menu"
          >
            <Menu size={20} />
          </button>
          <div className="pointer-events-none">
            <img src={logo} alt="Agility Logo" className="h-[112px] w-auto object-contain object-left" />
          </div>
        </div>

        <div className="flex items-center gap-6">
          <div ref={notifRef} className="relative">
            <button
              type="button"
              aria-label="Notifications"
              aria-expanded={showNotifications}
              aria-haspopup="true"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowNotifications((p) => !p);
                setShowDropdown(false);
              }}
              className="relative p-1.5 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
            >
              <Bell className="text-slate-500" size={18} strokeWidth={2} />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-0.5 rounded-full bg-red-500 flex items-center justify-center text-[9px] font-bold text-white leading-none">
                  {badgeCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <div className="absolute right-0 top-full mt-2 w-[min(360px,calc(100vw-2rem))] bg-white border border-slate-200 rounded-xl shadow-xl z-[70] overflow-hidden flex flex-col max-h-[min(480px,70vh)]">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-800">Notifications</span>
                    {unreadCount > 0 && (
                      <span className="text-xs bg-red-100 text-red-600 font-semibold px-1.5 py-0.5 rounded-full">
                        {unreadCount} new
                      </span>
                    )}
                  </div>
                  {unreadCount > 0 && (
                    <button
                      type="button"
                      onClick={markAllRead}
                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors"
                    >
                      <CheckSquare size={12} />
                      Mark all read
                    </button>
                  )}
                </div>

                <div className="overflow-y-auto flex-1">
                  {isLoading ? (
                    <div className="flex items-center justify-center py-10">
                      <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-10 text-slate-400">
                      <Bell size={28} className="mb-2 opacity-40" />
                      <p className="text-sm">No notifications yet</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {notifications.map((n) => (
                        <NotificationItem
                          key={n._id}
                          notification={n}
                          onRead={markAsRead}
                          onNavigate={handleNotificationNavigate}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* User dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowDropdown((p) => !p);
                setShowNotifications(false);
              }}
              className="flex items-center gap-2 hover:bg-slate-100 rounded-lg p-2 transition-colors"
              aria-haspopup="menu"
              aria-expanded={showDropdown}
            >
              <div className="h-9 w-9 rounded-full bg-primary-500 flex items-center justify-center text-white text-sm font-semibold">
                {getUserInitials()}
              </div>

              <div className="flex flex-col items-start leading-tight">
                <span className="text-xs font-semibold text-slate-800 truncate max-w-[160px]">
                  {getUserDisplayName()}
                </span>
                <span className="text-[10px] text-slate-500 uppercase tracking-wide">
                  {getRoleDisplayName()}
                </span>
              </div>

              <ChevronDown
                className={`text-slate-400 transition-transform ${showDropdown ? 'rotate-180' : ''}`}
                size={14}
              />
            </button>

            {showDropdown && (
              <div
                className="absolute right-0 top-full mt-2 w-64 max-w-[90vw] bg-white border border-slate-200 rounded-lg shadow-lg py-2 z-[70] pointer-events-auto"
                role="menu"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-4 py-2 border-b border-slate-100">
                  <p className="text-sm font-medium text-slate-800 break-words">{getUserDisplayName()}</p>
                  <p className="text-xs text-slate-500 break-all">{user?.email}</p>
                </div>

                <button
                  type="button"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer"
                  role="menuitem"
                >
                  <LogOut size={16} />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Backdrop only for user menu — notification panel uses click-outside so it stays above */}
      {showDropdown && (
        <div
          className="fixed inset-0 z-[50]"
          onMouseDown={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};
